<template>
  <div class="mall-nav-wrap mall-nav-wrap--simple">
    <FixedRightBar v-if="!isErpBuySite && config.showDefaultRightBar !== false" />

    <!-- 内联关键布局样式，避免首屏样式未就绪时出现错乱/换行 -->
    <div class="simple-bar"   :style="{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'nowrap',
          gap: '24px',
          padding: '12px clamp(20px, 4vw, 48px)',
          minHeight: '56px'
        }">
      <a v-if="!isErpBuySite" class="simple-logo" :href="`/`">
        <img
          v-if="showLogoImage"
          :style="logoStyle"
          class="logo-item"
          :src="config.imgURL"
          :alt="brandName"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          referrerpolicy="no-referrer"
        />
        <span v-else class="simple-logo-text">{{ brandName }}</span>
      </a>

      <div class="simple-menu" v-if="!isErpBuySite" :style="simpleMenuInlineStyle">
        <div class="navigation-item" v-for="(item, index) in normalizedNavigationList" :key="index">
          <a
            v-if="item.linkUrl&&item.linkUrl.indexOf('http')==0"
            :class="{
              'navigation-item-title': true,
              'navigation-item-title-active': actionIndex === index,
              ['navigation-item-title-' + index]: true,
            }"
            :style="getNavigationItemStyle(index)"
            :href="item.linkUrl"
            @click="trackNavigationItem(item, index)"
          >
            <LazyImage
              v-if="item.linkIcon"
              class="navigation-item-icon"
              :src="item.linkIcon"
              :use-lazy="false"
              :tint-color="getNavigationIconTintColor(item, index)"
            />
            {{ getLocaleText(item.title) }}
          </a>
          <NuxtLink
            v-else
            :class="{
              'navigation-item-title': true,
              'navigation-item-title-active': actionIndex === index,
              ['navigation-item-title-' + index]: true,
            }"
            :style="getNavigationItemStyle(index)"
            :to="item.linkUrl"
            :target="getJumpType?getJumpType(item, true):undefined"
            @click="trackNavigationItem(item, index)"
          >
            <LazyImage
              v-if="item.linkIcon"
              class="navigation-item-icon"
              :src="item.linkIcon"
              :use-lazy="false"
              :tint-color="getNavigationIconTintColor(item, index)"
            />
            {{ getLocaleText(item.title) }}
          </NuxtLink>
        </div>
      </div>

      <div class="simple-right" v-if="!isErpBuySite" :style="simpleRightInlineStyle">
        <div class="simple-locale-switch">
          <LocaleCurrencyChange :theme-vars="themeVars" />
        </div>
        <RightNavItems
          v-if="config.rightNavigationList && config.rightNavigationList.length"
          class="simple-right-nav-list"
          :items="config.rightNavigationList || []"
          :get-locale-text="getLocaleText"
          item-class="simple-right-nav-item"
          :theme-vars="themeVars"
        />
        <MallAccountEntry
          class="user-info-wrap"
          :mall-user-info="mallUserInfo"
          :logout="logout"
          variant="simple"
          :theme-vars="themeVars"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useMainStore } from "~/composables/store";
import LazyImage from "@/components/common/LazyImage/index.vue";
import FixedRightBar from "./FixedRightBar.vue";
import LocaleCurrencyChange from "./LocaleCurrencyChange.vue";
import RightNavItems from "./RightNavItems.vue";
import MallAccountEntry from "./MallAccountEntry.vue";
import { legacyPathToUserCenterPath } from "~/user-center/routes";
import { useRoute } from "#app";
import { trackLowCodeEvent } from "@/utils/lowCodeTracking.js";

const store = useMainStore();
const route = useRoute();
const props = defineProps({
  config: { type: Object, default: () => ({}) },
  themeVars: { type: Object, default: () => ({}) },
  isErpBuySite: { type: Boolean, default: false },
  showNavigationList: { type: Array, default: () => [] },
  actionIndex: { type: Number, default: 0 },
  mallUserInfo: { type: Object, default: () => ({}) },
  logout: { type: Function, required: true },
  getLocaleText: { type: Function, required: true },
  getJumpType: { type: Function, required: true },
});
const normalizedNavigationList = computed(() => {
  return (props.showNavigationList || []).map(item => ({
    ...item,
    linkUrl: legacyPathToUserCenterPath(String(item?.linkUrl || "")),
  }));
});
const brandName = computed(() => {
  return String(
    props.config?.logoText
    || store.getSiteInfo()?.pc_SITE_NAME
    || store.getSiteInfo()?.siteName
    || store.getSiteInfo()?.websiteName
    || "Mall"
  ).trim();
});
const showLogoImage = computed(() => Boolean(String(props.config?.imgURL || "").trim()));

const logoStyle = computed(() => {
  const w = Number(props.config?.imgWidth) || 160;
  const h = Number(props.config?.imgHeight) || 44;
  return {
    width: `${w}px`,
    height: `${h}px`,
    minWidth: `${w}px`,
    minHeight: `${h}px`,
    objectFit: props.config?.fit || "contain",
    display: "block",
  };
});

function getNavigationItemStyle(index) {
  const activeColor =
    (props.config.textConfigs && props.config.textConfigs.fontActionColor)
    || props.themeVars?.navActiveTextColor
    || "var(--el-color-primary-dark-1)";
  const defaultColor =
    (props.config.textConfigs && props.config.textConfigs.fontColor)
    || props.themeVars?.navTextColor
    || "#57534e";
  return {
    fontSize: ((props.config.textConfigs && props.config.textConfigs.fontSize) || 14) + "px",
    fontWeight: (props.config.textConfigs && props.config.textConfigs.fontWeight) || 500,
    color: props.actionIndex === index ? activeColor : defaultColor,
  };
}

function getNavigationIconTintColor(item, index) {
  if (!item?.linkIcon) {
    return "";
  }
  const isActive = props.actionIndex === index;
  if (isActive) {
    return item?.iconActiveTintColor || item?.iconTintColor || "";
  }
  return item?.iconTintColor || "";
}

function trackNavigationItem(item, index) {
  trackLowCodeEvent(item?.trackingConfig, {
    trigger: "click",
    element: item,
    elementType: "navigation_item",
    elementIndex: index,
    page: { fullPath: route.fullPath, path: route.path },
  });
}

// 这些是首屏“兜底”的关键布局样式：即使 scoped CSS 还没挂上，也能保持一行布局
 

const simpleMenuInlineStyle =   {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flex: "1 1 auto",
  minWidth: 0,
  overflowX: "auto",
  overflowY: "hidden",
  whiteSpace: "nowrap",
  gap: "24px",
};

const simpleRightInlineStyle =   {
  display: "flex",
  alignItems: "center",
  flex: "0 0 auto",
  flexWrap: "nowrap",
  whiteSpace: "nowrap",
  gap: "16px",
};
</script>

<style scoped lang="less">
.mall-nav-wrap--simple {
  width: 100%;
  background-color: v-bind("themeVars.navBackgroundColor");
 
}

.simple-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 24px;
  padding: 12px clamp(20px, 4vw, 48px);
  min-height: 56px;
}

.simple-logo {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  max-width: 220px;
  color: v-bind("themeVars.navTextColor");
  text-decoration: none;
}

.simple-logo-text {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: "WenQuanYi Zen Hei", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  font-size: 20px;
  line-height: 28px;
  font-weight: 500;
  color: v-bind("themeVars.navTextColor");
}

.simple-menu {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  align-self: stretch;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 24px;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  .navigation-item {
    display: flex;
    align-items: center;
    height: 100%;
  }
  .navigation-item-title {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 100%;
    padding: 0;
    text-decoration: none;
    white-space: nowrap;
    line-height: 20px;
    letter-spacing: -0.35px;
    font-family: "Manrope", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
    transition: color 0.2s ease, opacity 0.2s ease;
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      bottom: 3px;
      height: 2px;
      border-radius: 999px;
      background-color: currentColor;
      opacity: 0;
      transform: scaleX(0);
      transform-origin: center;
      transition: transform 0.2s ease, opacity 0.2s ease;
    }
    &:hover {
      color: v-bind("themeVars.navActiveTextColor") !important;
    }
  }
  .navigation-item-title-active {
    font-weight: 600 !important;

    // &::after {
    //   opacity: 1;
    //   transform: scaleX(1);
    // }
  }
}

.navigation-item-icon {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}

.simple-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  white-space: nowrap;
  margin-left: auto;
}

.simple-locale-switch {
  display: inline-flex;
  align-items: center;
}

.simple-right-nav-list {
  display: inline-flex;
  align-items: center;
}

.simple-right-nav-list :deep(.simple-right-nav-item) {
  font-size: 14px;
  line-height: 20px;
  color: v-bind("themeVars.navTextColor");
  font-weight: 500;
  font-family: "Manrope", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
}

.simple-right-nav-list :deep(.simple-right-nav-item__icon) {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.simple-locale-switch :deep(.switch-wrapper) {
  height: 40px;
  padding: 0 12px;
  margin-left: 0 !important;
  justify-content: flex-start;
  border-radius: 999px;
}

.simple-locale-switch :deep(.switch-wrapper:hover),
.simple-locale-switch :deep(.switch-wrapper.is-show) {
  background-color: v-bind("themeVars.navPanelHoverBackground");
}

.user-info-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 12px;
 
  min-width: 0;
  .avatar-link {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    text-decoration: none;
  }
  .user-info-box {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    color: #a8a29e;
    .user-link {
      font-size: 14px;
      line-height: 20px;
      color: v-bind("themeVars.navTextColor");
      text-decoration: none;
      font-weight: 500;
      display: inline-block;
      white-space: nowrap;
      font-family: "Manrope", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
      &:hover {
        opacity: 0.72;
      }
    }
    .user-name {
      max-width: 112px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .user-divider {
      display: inline-block;
      width: 1px;
      height: 22px;
      background-color: v-bind("themeVars.navDividerColor");
      flex: 0 0 auto;
    }
  }
}

.logo-item {
  cursor: pointer;
}

@media (max-width: 1200px) {
  .simple-bar {
    gap: 20px;
    padding: 12px 24px;
  }

  .simple-menu {
    gap: 18px;
  }
}
</style>
